import {
	type EntityDefinition,
	type FieldDefinition,
	liveFields,
	type RelationDefinition,
} from "@/shared/contracts/structure";
import { say } from "../say";

/** Native process state uses the same renderers, without becoming a user-defined column. */
export function recordFields(
	entity: EntityDefinition,
	relations: readonly RelationDefinition[] = [],
): FieldDefinition[] {
	const fields = [...liveFields(entity)];
	for (const relation of relations.filter((r) => r.kind === "many_to_one")) {
		const code = relation.fieldCode;
		if (!fields.some((f) => f.code === code))
			fields.push({
				id: relation.id,
				code,
				label: relation.label,
				type: "ref_entity",
				required: relation.required,
				config: { entity: relation.toEntityCode },
				helpText: null,
				sortOrder: fields.length,
				showInList: true,
				archived: false,
			});
	}
	if (!entity.process?.stages.length) return fields;
	return [
		...fields,
		{
			id: "stage",
			code: "stage",
			label: say("stage"),
			type: "select",
			required: true,
			config: {
				options: entity.process.stages.map((s) => ({
					value: s.code,
					label: s.name,
					color: s.color,
				})),
			},
			helpText: say("stageHint"),
			sortOrder: fields.length,
			showInList: true,
			archived: false,
		},
	];
}

/**
 * "MOSTRAR SÓ NAS ETAPAS" (`config.showAtStages`): as etapas do funil em que o
 * campo aparece. Vazio = em todas; um código que o funil não tem não conta.
 */
export function shownAtStage(
	field: FieldDefinition,
	entity: EntityDefinition,
	stage: unknown,
): boolean {
	const raw = field.config?.showAtStages;
	const stages = entity.process?.stages ?? [];
	const at = Array.isArray(raw) ? raw.filter((code) => stages.some((s) => s.code === code)) : [];
	return at.length === 0 || at.includes(stage);
}

/**
 * "MOSTRAR SÓ QUANDO" (`config.showWhen: { field, in }`), irmão de `showAtStages`: o
 * campo aparece só quando outro campo do mesmo registro tem um desses valores (num
 * multi_select, basta um). Sem condição, ou lendo um campo arquivado, aparece.
 */
export function shownWhen(
	field: FieldDefinition,
	entity: EntityDefinition,
	values: Record<string, unknown>,
): boolean {
	const when = field.config?.showWhen as { field?: unknown; in?: unknown } | undefined;
	if (!when || typeof when.field !== "string" || !Array.isArray(when.in)) return true;
	const accepted = when.in;
	if (!liveFields(entity).some((f) => f.code === when.field)) return true;
	const value = values[when.field];
	// Um boolean vazio lê-se "não", como no servidor.
	return (Array.isArray(value) ? value : [value ?? false]).some((v) => accepted.includes(v));
}

/** O que a etapa PEDE ao entrar nela: os obrigatórios que só aparecem lá. */
export function askedAtStage(entity: EntityDefinition, stage: string): FieldDefinition[] {
	return liveFields(entity).filter(
		(field) =>
			field.required &&
			Array.isArray(field.config?.showAtStages) &&
			field.config.showAtStages.includes(stage),
	);
}

/**
 * Uma CHAVE que só funções escrevem ("Chave de geração"): única, opcional, e fora da
 * tela de formulário que o construtor declarou. Não é pergunta nem leitura de ninguém.
 * Sem tela declarada (`layout` vazio) nada é chave — não há contra o que comparar.
 */
export function internalKey(
	field: FieldDefinition,
	layout: readonly string[] | null | undefined,
): boolean {
	const declared = layout ?? [];
	return (
		declared.length > 0 &&
		Boolean(field.config?.unique) &&
		!field.required &&
		!declared.includes(field.code)
	);
}

/**
 * O QUE UMA ETAPA RECUSADA PEDE: o campo que a regra nomeia, os campos que a expressão
 * dela cita e que estão vazios neste registro, e os obrigatórios daquela etapa. É o
 * formulário de três campos no lugar do de trinta. A regra é achada pela frase (o que
 * o servidor devolve); sem ela, só o campo nomeado. Vazio = abrir o formulário inteiro.
 */
export function fieldsAskedBy(
	entity: EntityDefinition,
	refusal: { stage: string; message: string; field: string | null },
	record: Record<string, unknown>,
): string[] {
	const live = new Set(liveFields(entity).map((f) => f.code));
	const rule = entity.invariants?.find((r) => r.message === refusal.message);
	const empty = (code: string) => record[code] == null || record[code] === "";
	// Os nomes fora das aspas: `'disponivel'` é um valor, não um campo.
	const words = rule?.expr.replace(/'[^']*'/g, " ").match(/[a-z_][a-z0-9_]*/gi) ?? [];
	const cited = words.filter((code) => live.has(code) && empty(code));
	const asked = askedAtStage(entity, refusal.stage).map((f) => f.code);
	return [...new Set([...(refusal.field ? [refusal.field] : []), ...cited, ...asked])];
}
